import { Clock3, Cpu, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useEntityLog } from "@/shared/hooks/use-workflows";
import { Badge } from "@/shared/components/ui/badge";
import type { EntityLogEntry } from "@/shared/lib/workflows/types";
import { cn } from "@/shared/lib/utils";
import { CardDragHandle } from "@/shared/components/card-drag-handle";
import { sentenceCaseKey } from "@/shared/lib/humanise";
import { pastTenseActivityTitle } from "@/shared/lib/workflows/activity-title";
import {
  addressDisplay,
  isAddressLike,
  useFormatters,
} from "@/shared/lib/formatting";

export function EntityHistoryCard({
  workflowInstanceUuid,
  entityKey,
  entityId,
  entityKeys,
  entityIds,
  title = "Entity history",
  className,
}: {
  workflowInstanceUuid?: string | null;
  entityKey?: string;
  entityId?: string | null;
  // A single Activity trail can aggregate several related entities (e.g. a
  // registration's holding + host + consumer). Pass their keys/ids here and
  // the log query merges + sorts them server-side.
  entityKeys?: Array<string | null | undefined>;
  entityIds?: Array<string | null | undefined>;
  title?: string;
  className?: string;
}) {
  const mergedKeys = [...(entityKey ? [entityKey] : []), ...(entityKeys ?? [])]
    .filter((k): k is string => Boolean(k))
    .join(",");
  const mergedIds = [...(entityId ? [entityId] : []), ...(entityIds ?? [])]
    .filter((i): i is string => Boolean(i))
    .join(",");

  const {
    data: entries = [],
    isLoading,
    error,
  } = useEntityLog({
    workflow_instance: workflowInstanceUuid || undefined,
    entity_key: mergedKeys || undefined,
    entity_id: mergedIds || undefined,
    page_size: 50,
  });

  return (
    // Same shell as the other detail cards (DetailCard): hairline border,
    // fixed-height subtle header with an uppercase title + icon, white body.
    <div
      className={cn(
        "rounded-[8px] border-[0.5px] border-[var(--rule)]",
        className,
      )}
    >
      <div className="flex h-[48px] items-center justify-between gap-2 rounded-t-[8px] bg-[var(--bg-subtle)] px-4">
        <div className="flex min-w-0 items-center gap-2">
          <CardDragHandle />
          <span className="shrink-0 text-[var(--ink-3)]">
            <Clock3 className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-xs font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {title}
          </span>
        </div>
      </div>

      {/* Body - the claims-style activity timeline, scrollable when long. */}
      <div className="rounded-b-[8px] bg-[var(--bg)]">
        {isLoading ? (
          <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">
            Loading history...
          </p>
        ) : error ? (
          <p className="px-4 py-3 text-[13px] text-[var(--status-error)]">
            Could not load entity history.
          </p>
        ) : entries.length === 0 ? (
          <p className="px-4 py-3 text-[13px] text-[var(--ink-3)]">
            No entity history has been recorded yet.
          </p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto px-4 py-4">
            <div className="pl-5">
              {entries.map((entry, index) => (
                <EntityHistoryEntry
                  key={entry.uuid}
                  entry={entry}
                  showConnector={index < entries.length - 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const RECORD_EDIT_EVENT_TYPES = new Set(["attribute_change", "entity_change"]);

function AttributeChangeSummary({ entry }: { entry: EntityLogEntry }) {
  const { t } = useTranslation();
  if (entry.event_type !== "attribute_change") return null;
  const blank = t("activity.blankValue", "blank");
  const oldVals = entry.old_values;
  const newVals = entry.new_values;
  const keys = [
    ...new Set([...Object.keys(oldVals), ...Object.keys(newVals)]),
  ];
  if (keys.length === 0) return null;

  return (
    <div className="mt-2 space-y-1">
      {keys.map((key) => {
        const oldVal = formatLogValue(oldVals[key]);
        const newVal = formatLogValue(newVals[key]);
        if (oldVal === newVal) return null;
        return (
          <p key={key} className="text-[12px] leading-relaxed text-[var(--ink-2)]">
            <span className="font-medium">{sentenceCaseKey(key)}</span>
            {": "}
            {/* Always before -> after, so a value that was added reads
                differently from one that was corrected or cleared. */}
            {oldVal ? (
              <span className="line-through text-[var(--ink-3)]">{oldVal}</span>
            ) : (
              <span className="text-[var(--ink-3)]">{blank}</span>
            )}
            {" → "}
            {newVal ? (
              <span>{newVal}</span>
            ) : (
              <span className="text-[var(--ink-3)]">{blank}</span>
            )}
          </p>
        );
      })}
    </div>
  );
}


function formatLogValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  // Composite ADDRESS values (e.g. a mailing-address change) are stored as a
  // structured object. Render the human-readable address rather than letting
  // it fall through to String(value) -> "[object Object]", which both hides the
  // real value and collapses the old -> new diff.
  if (isAddressLike(value)) return addressDisplay(value);
  return String(value);
}

function EntityHistoryEntry({
  entry,
  showConnector,
}: {
  entry: EntityLogEntry;
  showConnector: boolean;
}) {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const resolvedIssueSummary = getResolvedIssueSummary(entry);
  const isSystem = entry.actor_type === "SYSTEM";
  const actor =
    entry.user_name ||
    entry.system_actor_key ||
    (isSystem ? "System" : "User");
  // A record edit reads as what happened ("Dealer changed"), and carries no
  // category chip: "attribute change" / "entity change" are the system's
  // filing terms, and the title already says what changed.
  const isRecordEdit = RECORD_EDIT_EVENT_TYPES.has(entry.event_type);
  const label =
    entry.action_label ||
    entry.action_key ||
    entry.event_type.replace(/_/g, " ");

  return (
    <div className="relative pb-[18px] pl-3">
      {/* Connector line - rendered per entry so the rail stops at the last
          dot instead of hanging below it. Centered on the left-[-20px] dot
          (11px wide, so its centre sits at -14.5px) and starting just below
          the dot so it links cleanly into the next entry's dot. */}
      {showConnector && (
        <div className="absolute bottom-0 left-[-15px] top-4 w-px bg-[var(--rule)]" />
      )}
      <div
        className={cn(
          "absolute left-[-20px] top-1 h-[11px] w-[11px] rounded-full border-[1.5px] bg-[var(--bg)]",
          entityLogDotClass(entry.event_type),
        )}
      />
      <div>
        <span className="text-[13px] font-normal text-[var(--ink)]">
          {isRecordEdit ? pastTenseActivityTitle(label, t) : label}
        </span>
        <div className="mt-1 flex items-center gap-2">
          {isSystem ? (
            <Cpu className="h-3 w-3 text-[var(--ink-3)]" />
          ) : (
            <User className="h-3 w-3 text-[var(--ink-3)]" />
          )}
          <span className="text-[11px] text-[var(--ink-3)]">{actor}</span>
          {isRecordEdit ? null : (
            <Badge variant="outline" className="shrink-0 text-[10px]">
              {entry.event_type.replace(/_/g, " ")}
            </Badge>
          )}
          <span className="text-[10px] text-[var(--ink-3)]">
            {formatDateTime(entry.occurred_at)}
          </span>
        </div>
        <ExceptionReasonsSummary entry={entry} />
        {resolvedIssueSummary ? (
          <p className="mt-2 text-[12px] leading-relaxed text-[var(--ink-2)]">
            Issue: <span className="font-medium">{resolvedIssueSummary}</span>
          </p>
        ) : null}
        <EditReasonSummary entry={entry} />
        {entry.comment ? (
          <p
            className={cn(
              "text-[12px] leading-relaxed text-[var(--ink-2)]",
              resolvedIssueSummary ? "mt-1" : "mt-2",
            )}
          >
            {entry.comment}
          </p>
        ) : null}
        <AttributeChangeSummary entry={entry} />
      </div>
    </div>
  );
}

function ExceptionReasonsSummary({ entry }: { entry: EntityLogEntry }) {
  if (entry.event_type !== "exception_granted") return null;
  const reasons = entry.new_values?.exception_reasons;
  if (!Array.isArray(reasons) || reasons.length === 0) return null;

  const labels = reasons.map((r: unknown) => {
    if (isRecord(r) && typeof r.title === "string") return r.title;
    if (typeof r === "string") return sentenceCaseKey(r);
    return null;
  }).filter(Boolean) as string[];

  if (labels.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {labels.map((label) => (
        <Badge key={label} variant="secondary" className="text-[10px]">
          {label}
        </Badge>
      ))}
    </div>
  );
}

function EditReasonSummary({ entry }: { entry: EntityLogEntry }) {
  if (
    entry.event_type !== "attribute_change" &&
    entry.event_type !== "entity_change"
  ) {
    return null;
  }
  const reason = entry.metadata?.reason;
  if (typeof reason !== "string" || !reason.trim()) return null;
  return (
    <p className="mt-2 text-[12px] leading-relaxed text-[var(--ink-2)]">
      Reason: <span className="font-medium">{reason}</span>
    </p>
  );
}

function getResolvedIssueSummary(entry: EntityLogEntry): string {
  if (entry.event_type !== "workflow_issue") return "";
  if (
    entry.action_key !== "issue_resolved" &&
    entry.action_key !== "issue_dismissed"
  ) {
    return "";
  }

  const capturedValues = entry.new_values.captured_values;
  if (!isRecord(capturedValues)) return "";

  const summary = capturedValues.summary;
  return typeof summary === "string" ? summary.trim() : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function entityLogDotClass(eventType: string): string {
  if (eventType === "exception_granted") return "border-[var(--green)]";
  if (eventType === "workflow_issue") return "border-[var(--gold)]";
  if (eventType === "attribute_change") return "border-[var(--ink-2)]";
  return "border-[var(--ink-3)]";
}
